import { useEffect, useState } from "react";
import { Plus, Trash, CheckCircle, FileText, Scales, CalendarCheck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, shortDate, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

function Panel({ children, className = "" }) {
  return <div className={`bg-card border border-border rounded-md p-6 ${className}`}>{children}</div>;
}

const AMD_TYPES = ["Scope", "Time Extension", "Funding Increase", "Funding Decrease", "Other"];
const STATUS_CLS = {
  submitted: "bg-sky-50 text-sky-800 border-sky-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
};

/* ---------------- Amendments + Budget Realignments ---------------- */
export function AmendmentsTab({ c, isAdmin, reload }) {
  const [amds, setAmds] = useState([]);
  const [reas, setReas] = useState([]);
  const [form, setForm] = useState({ type: "Scope", description: "", amount_change: "", new_end_date: "" });
  const items = c.budget?.line_items || [];
  const [adj, setAdj] = useState({});
  const [reaDesc, setReaDesc] = useState("");

  const load = async () => {
    setAmds((await api.get(`/contracts/${c.id}/amendments`)).data);
    setReas((await api.get(`/contracts/${c.id}/realignments`)).data);
  };
  useEffect(() => { load(); setAdj(Object.fromEntries(items.map((i) => [i.id, i.amount]))); /* eslint-disable-next-line */ }, [c.id]);

  const raiseAmd = async () => {
    if (!form.description) return toast.error("Description required");
    try {
      await api.post(`/contracts/${c.id}/amendments`, { ...form, amount_change: parseFloat(form.amount_change) || 0 });
      toast.success("Amendment raised");
      setForm({ type: "Scope", description: "", amount_change: "", new_end_date: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const approveAmd = async (id) => { await api.post(`/contracts/amendments/${id}/approve`.replace("/contracts", "")); };

  const netChange = items.reduce((s, i) => s + ((parseFloat(adj[i.id]) || 0) - (i.amount || 0)), 0);

  const raiseRea = async () => {
    if (!reaDesc) return toast.error("Description required");
    const adjustments = items.filter((i) => (parseFloat(adj[i.id]) || 0) !== i.amount)
      .map((i) => ({ line_item_id: i.id, to_amount: parseFloat(adj[i.id]) || 0 }));
    if (!adjustments.length) return toast.error("Change at least one line item amount");
    await api.post(`/contracts/${c.id}/realignments`, { description: reaDesc, adjustments });
    toast.success("Realignment raised");
    setReaDesc("");
    load();
  };

  return (
    <div className="space-y-6">
      {/* Amendments */}
      <Panel>
        <div className="flex items-center gap-2 mb-4"><FileText size={20} className="text-primary" /><span className="font-head font-semibold">Contract Amendments</span></div>
        {isAdmin && (
          <div className="grid sm:grid-cols-5 gap-3 mb-5">
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="amd-type"><SelectValue /></SelectTrigger>
                <SelectContent>{AMD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="overline text-muted-foreground">Description</Label><Input data-testid="amd-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Amount Δ ($)</Label><Input data-testid="amd-amount" type="number" value={form.amount_change} onChange={(e) => setForm({ ...form, amount_change: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">New end date</Label><Input data-testid="amd-end" type="date" value={form.new_end_date} onChange={(e) => setForm({ ...form, new_end_date: e.target.value })} /></div>
            <div className="sm:col-span-5"><Button data-testid="amd-raise" onClick={raiseAmd}><Plus size={15} className="mr-1" /> Raise Amendment</Button></div>
          </div>
        )}
        {amds.length === 0 ? <p className="text-sm text-muted-foreground">No amendments.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead>Amount Δ</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {amds.map((a) => (
                <TableRow key={a.id} data-testid={`amd-row-${a.id}`}>
                  <TableCell className="text-sm font-medium">{a.amendment_number}</TableCell>
                  <TableCell className="text-sm">{a.type}</TableCell>
                  <TableCell className="text-sm">{a.description}</TableCell>
                  <TableCell className="text-sm">{money(a.amount_change)}</TableCell>
                  <TableCell><span className={`inline-flex px-2.5 py-0.5 rounded-sm border text-xs font-medium ${STATUS_CLS[a.status]}`}>{a.status}</span></TableCell>
                  <TableCell>
                    {isAdmin && a.status === "submitted" && (
                      <Button size="sm" data-testid={`amd-approve-${a.id}`} onClick={async () => { await api.post(`/amendments/${a.id}/approve`); toast.success("Amendment approved"); load(); reload(); }}>
                        <CheckCircle size={14} className="mr-1" /> Approve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* Realignments */}
      <Panel>
        <div className="flex items-center gap-2 mb-4"><Scales size={20} className="text-primary" /><span className="font-head font-semibold">Budget Realignments</span></div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Add structured budget line items first (Budget tab) to realign funds.</p>
        ) : isAdmin ? (
          <div className="space-y-4">
            <Table>
              <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Current</TableHead><TableHead>New Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="text-sm">{i.category} <span className="text-muted-foreground">· {i.description}</span></TableCell>
                    <TableCell className="text-sm">{money(i.amount)}</TableCell>
                    <TableCell><Input data-testid={`rea-adj-${i.id}`} type="number" className="w-32" value={adj[i.id] ?? i.amount} onChange={(e) => setAdj({ ...adj, [i.id]: e.target.value })} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Net change: <b className={netChange === 0 ? "text-foreground" : "text-primary"}>{money(netChange)}</b></span>
            </div>
            <div className="flex gap-2">
              <Input data-testid="rea-desc" value={reaDesc} onChange={(e) => setReaDesc(e.target.value)} placeholder="Reason for realignment…" />
              <Button data-testid="rea-raise" onClick={raiseRea}>Raise Realignment</Button>
            </div>
          </div>
        ) : <p className="text-sm text-muted-foreground">Only administrators can propose realignments.</p>}

        {reas.length > 0 && (
          <div className="mt-6">
            <div className="overline text-muted-foreground mb-2">Realignment history</div>
            <Table>
              <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Description</TableHead><TableHead>Net Δ</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {reas.map((r) => (
                  <TableRow key={r.id} data-testid={`rea-row-${r.id}`}>
                    <TableCell className="text-sm font-medium">{r.realignment_number}</TableCell>
                    <TableCell className="text-sm">{r.description}</TableCell>
                    <TableCell className="text-sm">{money(r.net_change)}</TableCell>
                    <TableCell><span className={`inline-flex px-2.5 py-0.5 rounded-sm border text-xs font-medium ${STATUS_CLS[r.status]}`}>{r.status}</span></TableCell>
                    <TableCell>{isAdmin && r.status === "submitted" && (
                      <Button size="sm" data-testid={`rea-approve-${r.id}`} onClick={async () => { await api.post(`/realignments/${r.id}/approve`); toast.success("Realignment applied"); load(); reload(); }}>
                        <CheckCircle size={14} className="mr-1" /> Apply
                      </Button>
                    )}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/* ---------------- Quarterly Reporting (grant) ---------------- */
const RPT_STATUS = {
  not_started: "bg-slate-100 text-slate-700 border-slate-300",
  submitted: "bg-sky-50 text-sky-800 border-sky-300",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-300",
  overdue: "bg-alert/10 text-alert border-alert/40",
};

export function QuarterlyReportsTab({ grant, canManage }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ period_label: "", due_date: "" });

  const load = async () => setRows((await api.get(`/grants/${grant.id}/reports`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [grant.id]);

  const generate = async () => { const { data } = await api.post(`/grants/${grant.id}/reports/generate`); toast.success(`${data.created} periods generated`); load(); };
  const add = async () => {
    if (!form.period_label || !form.due_date) return toast.error("Period and due date required");
    await api.post(`/grants/${grant.id}/reports`, form);
    setForm({ period_label: "", due_date: "" });
    load();
  };
  const setStatus = async (id, status) => { await api.put(`/reports/${id}`, { status }); load(); };
  const remove = async (id) => { await api.delete(`/reports/${id}`); load(); };

  const overdueCount = rows.filter((r) => r.status === "overdue").length;

  return (
    <div className="space-y-5">
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2"><CalendarCheck size={20} className="text-primary" /><span className="font-head font-semibold">Reporting Schedule</span>
            {overdueCount > 0 && <Badge className="bg-alert/10 text-alert border-alert/40 ml-2"><Warning size={12} className="mr-1" />{overdueCount} overdue</Badge>}
          </div>
          {canManage && <Button size="sm" variant="outline" data-testid="qr-generate" onClick={generate}>Generate 4 quarters</Button>}
        </div>
        {canManage && (
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Period label</Label><Input data-testid="qr-period" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="Q1 · 2026" /></div>
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Due date</Label><Input data-testid="qr-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div className="flex items-end"><Button data-testid="qr-add" className="w-full" onClick={add}><Plus size={15} className="mr-1" /> Add period</Button></div>
          </div>
        )}
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">No reporting periods scheduled.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Due</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`qr-row-${r.id}`} className={r.status === "overdue" ? "bg-alert/5" : ""}>
                  <TableCell className="text-sm font-medium">{r.period_label}</TableCell>
                  <TableCell className="text-sm">{shortDate(r.due_date)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.submitted_date ? shortDate(r.submitted_date) : "—"}</TableCell>
                  <TableCell>
                    {canManage ? (
                      <Select value={r.status === "overdue" ? "not_started" : r.status} onValueChange={(v) => setStatus(r.id, v)}>
                        <SelectTrigger data-testid={`qr-status-${r.id}`} className="w-40 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not started</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : <span className={`inline-flex px-2.5 py-0.5 rounded-sm border text-xs font-medium ${RPT_STATUS[r.status]}`}>{r.status.replace(/_/g, " ")}</span>}
                  </TableCell>
                  <TableCell>{canManage && <Button size="icon" variant="ghost" data-testid={`qr-del-${r.id}`} onClick={() => remove(r.id)}><Trash size={15} className="text-alert" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}
